import * as z from 'zod';
import { blockRefSchema, readBlockRefSchema } from '../blocks';
import { embeddedBlockSchema } from '../campaigns';
import { companyRefSchema, companySchema } from '../companies';
import { blockTypeSchema, cssSelectorMapSchema } from '../common';
import { pageOutdatedStatus, pageRefSchema, readPageRefSchema } from '../pages';

// 1. Reference schema with label field (for dropdowns, selects, links)
export const readBlockVariantRefSchema = z.object({
  id: z.uuid(),
  label: z.string().nullable(),
});
export type ReadBlockVariantRef = z.infer<typeof readBlockVariantRefSchema>;

// 2. Minimal reference schema (for foreign keys)
export const blockVariantRefSchema = z.object({
  id: z.uuid(),
});
export type BlockVariantRef = z.infer<typeof blockVariantRefSchema>;

// Embedded (denormalized) variant used inside the campaign read tree
// (campaign → page → blockVariant). `block` is the embedded block, referenced
// lazily to avoid a circular import with campaigns (which embeds this schema).
export const embeddedBlockVariantSchema = z.object({
  id: z.uuid().nullable(),
  block: z.lazy(() => embeddedBlockSchema).nullable(),
  cssSelectors: cssSelectorMapSchema.nullable(),
  value: z.string().nullable(),
  type: blockTypeSchema.nullable(),
  valueAsText: z.string().nullable(),
  status: z.string().nullable(),
  sectionCssSelector: z.string().nullable(),
  label: z.string().nullable(),
  createdAt: z.iso.datetime().nullable(),
  updatedAt: z.iso.datetime().nullable(),
  companies: z.array(companySchema).nullable(),
});
export type EmbeddedBlockVariant = z.infer<typeof embeddedBlockVariantSchema>;

// 3. Read schema - all properties nullable (API response).
// Shares its scalar fields with `embeddedBlockVariantSchema`, so we derive from
// it: swap the embedded `block` for a ref and add the fields that only exist on
// the standalone read (page, companies, type).
export const readBlockVariantSchema = embeddedBlockVariantSchema
  .omit({ block: true })
  .extend({
    block: readBlockRefSchema.nullable(),
    page: readPageRefSchema
      .extend({
        // Only present when asked for via the `fields` projection: writing a
        // variant can outdate the page's live version, and the server reports
        // the resulting status on the write response so callers don't have to
        // re-read the page (or restate the rule that decides it).
        outdatedStatus: pageOutdatedStatus.nullable().optional(),
      })
      .nullable(),
    // Ordered companies (logos), used when `type` is `customer_logo`;
    // Read-only — inherited from the parent block.
    type: blockTypeSchema.nullable(),
  });
export type ReadBlockVariant = z.infer<typeof readBlockVariantSchema>;

// 4. Create schema - based on DTO serialization groups
export const createBlockVariantSchema = z.object({
  block: blockRefSchema,
  page: pageRefSchema.optional(),
  cssSelectors: cssSelectorMapSchema,
  value: z.string().optional(),
  status: z
    .string()
    .max(100, 'Status must not exceed 100 characters')
    .optional(),
  sectionCssSelector: z
    .string()
    .max(1000, 'Section CSS selector must not exceed 1000 characters')
    .optional(),
  label: z.string().max(500, 'Label must not exceed 500 characters').optional(),
});
export type CreateBlockVariant = z.infer<typeof createBlockVariantSchema>;

// 5. Update schema - based on DTO serialization groups (usually partial)
export const updateBlockVariantSchema = z.object({
  page: pageRefSchema.optional(),
  cssSelectors: cssSelectorMapSchema.optional(),
  value: z.string().optional(),
  // Ordered companies (logos) for `customer_logo` variants. Sent as
  // refs — the server reduces them to ids on write.
  companies: z.array(companyRefSchema).optional(),
  status: z
    .string()
    .max(100, 'Status must not exceed 100 characters')
    .optional(),
  sectionCssSelector: z
    .string()
    .max(1000, 'Section CSS selector must not exceed 1000 characters')
    .optional(),
  label: z.string().max(500, 'Label must not exceed 500 characters').optional(),
});
export type UpdateBlockVariant = z.infer<typeof updateBlockVariantSchema>;

// Legacy export for backward compatibility
export const blockVariantSchema = readBlockVariantSchema;
export type BlockVariant = ReadBlockVariant;
